import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import AboutBigButton from "@/components/AboutBigButton";
import PhoneInput from "@/components/PhoneInput";
import { validatePhone } from "@/utils/phoneValidation";
import { ArrowLeft, User as UserIcon, WhatsappLogo, CheckCircle, Note } from "@phosphor-icons/react";

export default function DriverRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "",
    phone: "966",
    whatsapp_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("الاسم مطلوب");
    const phoneCheck = validatePhone(f.phone);
    if (!phoneCheck.ok) return toast.error(phoneCheck.reason || "رقم الجوال مطلوب");
    if (f.whatsapp_phone) {
      const waCheck = validatePhone(f.whatsapp_phone);
      if (!waCheck.ok) return toast.error(`واتساب: ${waCheck.reason}`);
    }
    setSubmitting(true);
    try {
      await api.post("/auth/driver-register", {
        name: f.name.trim(),
        phone: f.phone,
        whatsapp_phone: f.whatsapp_phone || f.phone,
        notes: f.notes.trim(),
      });
      setSubmitted(true);
      toast.success("تم إرسال طلبك بنجاح — بانتظار مراجعة الإدارة");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-8" dir="rtl">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo variant="brand" size={54} />
          <h1 className="text-2xl font-bold text-white mt-4">تسجيل سائق جديد</h1>
          <p className="text-white/50 text-sm mt-1">قدّم طلب انضمام إلى فريق مراسم الزوار</p>
        </div>

        {submitted ? (
          <div className="rounded-2xl border border-[#2E7D32]/50 bg-[#2E7D32]/10 p-6 text-center space-y-3" data-testid="driver-register-success">
            <CheckCircle size={48} weight="fill" className="text-[#2E7D32] mx-auto" />
            <h2 className="text-white font-bold text-lg">تم إرسال طلبك ✓</h2>
            <p className="text-white/75 text-sm leading-relaxed">
              ستراجع الإدارة طلبك وترسل لك رمز الدخول عبر واتساب على الرقم
              <span className="text-primary num mx-1" dir="ltr">
                +{(f.whatsapp_phone || f.phone).slice(0, 3)} {(f.whatsapp_phone || f.phone).slice(3)}
              </span>
              خلال أقرب وقت.
            </p>
            <p className="text-white/50 text-xs">
              نوع السيارة ورقم اللوحة سيتم تحديدهما من قبل الإدارة عند الموافقة.
            </p>
            <Link
              to="/login"
              className="inline-block mt-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm"
              data-testid="back-to-login"
            >
              العودة لتسجيل الدخول
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-primary/25 bg-card p-6">
            <div>
              <label className="block text-sm text-white/70 mb-2 text-right">الاسم الكامل *</label>
              <div className="relative">
                <UserIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60" />
                <input
                  required
                  value={f.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="mz-input text-right pr-9"
                  data-testid="dr-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2 text-right">رقم الجوال *</label>
              <PhoneInput value={f.phone} onChange={(v) => set("phone", v)} required testId="dr-phone" />
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2 text-right flex items-center gap-1 justify-end">
                <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" /> رقم واتساب (اختياري)
              </label>
              <PhoneInput
                value={f.whatsapp_phone}
                onChange={(v) => set("whatsapp_phone", v)}
                testId="dr-wa"
                placeholder="مطابق للجوال إذا تركته فارغاً"
              />
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2 text-right flex items-center gap-1 justify-end">
                <Note size={14} /> ملاحظات (اختياري)
              </label>
              <textarea
                rows={3}
                value={f.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="mz-input text-right"
                data-testid="dr-notes"
                placeholder="أي معلومات إضافية تودّ إرسالها للإدارة"
              />
            </div>

            <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-[11px] text-white/60 leading-relaxed">
              💡 نوع السيارة ورقم اللوحة سيتم تحديدهما بواسطة الإدارة عند الموافقة على طلبك.
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="dr-submit"
              className="w-full mt-2 bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold py-4 rounded-xl transition-all disabled:opacity-60"
            >
              {submitting ? "جارٍ الإرسال…" : "إرسال الطلب"}
            </button>

            <Link
              to="/login"
              data-testid="dr-back-login"
              className="block text-center text-primary hover:text-[#E1C484] text-sm mt-3 inline-flex items-center gap-1 justify-center w-full"
            >
              العودة لتسجيل الدخول <ArrowLeft size={14} />
            </Link>
            <AboutBigButton />
          </form>
        )}
      </div>
    </div>
  );
}
